import type { Path, UseFormReturn } from 'react-hook-form';
import type { FieldValues } from 'react-hook-form';

import { isValidNumber } from './helpers';

type FieldPath<T extends FieldValues> = Path<T>;

interface GenericValidationConfig<TFieldArrayItem extends Record<string, unknown>, TFormValues extends FieldValues> {
	arrayKey: keyof TFormValues;
	requiredFields?: (keyof TFieldArrayItem)[];
	numberFields?: (keyof TFieldArrayItem)[];
	uniqueFields?: (keyof TFieldArrayItem)[];
	fieldNameMapper?: Partial<Record<keyof TFieldArrayItem, string>>;
	uniqueCaseSensitive?: boolean;
	shouldValidateRow?: (row: TFieldArrayItem, rowIndex: number) => boolean;
}

export function setOneErrorToRHF<TFormValues extends FieldValues>(
	fieldPath: Path<TFormValues>,
	message: string,
	setError: UseFormReturn['setError']
) {
	let type = 'other';
	if (message.toLowerCase().includes('unique')) {
		type = 'tooltip';
	} else if (message.toLowerCase().includes('required')) {
		type = 'required';
	}

	setError(fieldPath, { type, message });
}

export function validateOneField<TFormValues extends Record<string, unknown>, TFieldArrayItem extends Record<string, unknown>>({
	cellName,
	fieldName,
	value,
	setError,
	config,
}: {
	cellName: FieldPath<TFormValues>;
	fieldName: keyof TFieldArrayItem;
	value: unknown;
	setError: UseFormReturn['setError'];
	config: GenericValidationConfig<TFieldArrayItem, TFormValues>;
}) {
	const {
		requiredFields = [],
		numberFields = [],
	} = config;

	// Required check
	if (requiredFields.includes(fieldName)) {
		const isEmpty = value === '' || value === null || value === undefined;
		if (isEmpty) {
			setOneErrorToRHF(cellName, 'Required', setError);
			return;
		}
	}

	// Number check
	if (numberFields?.includes(fieldName)) {
		if (value !== '' && value !== null && value !== undefined) {
			if (typeof value === 'string' && !isValidNumber(value)) {
				setOneErrorToRHF(cellName, 'Not a valid number.', setError);
			}
		}
	}
}

export function validateFieldArray<
	TFieldArrayItem extends Record<string, unknown>,
	TFormValues extends { [K in keyof TFormValues]: TFieldArrayItem[] }
>(
	values: TFormValues,
	setError: UseFormReturn['setError'],
	config: GenericValidationConfig<TFieldArrayItem, TFormValues>
) {
	const {
		arrayKey,
		requiredFields = [],
		numberFields = [],
		uniqueFields = [],
		fieldNameMapper = {},
		uniqueCaseSensitive = false,
		shouldValidateRow,
	} = config;

	const data = values[arrayKey];
	if (!Array.isArray(data)) {
		return;
	}

	// const seenMap: Partial<Record<keyof TFieldArrayItem, Map<string | number, number[]>>> = {};
	const seenMap: Partial<Record<keyof TFieldArrayItem, Record<string, number[]>>> = {};

	data.forEach((row, rowIndex) => {
		if (shouldValidateRow && !shouldValidateRow(row, rowIndex)) {
			return;
		}

		requiredFields.forEach((field) => {
			const value = row[field];
			const isEmpty = value === '' || value === null || value === undefined;
			if (isEmpty) {
				const path = `${String(arrayKey)}.${rowIndex}.${String(field)}` as FieldPath<TFormValues>;
				setOneErrorToRHF(path, 'Required', setError);
			}
		});

		numberFields.forEach((field) => {
			const value = row[field];
			if (value !== '' && value !== null && value !== undefined) {
				if ((typeof value === 'string' || typeof value === 'number') && !isValidNumber(value)) {
					const path = `${String(arrayKey)}.${rowIndex}.${String(field)}` as FieldPath<TFormValues>;
					setOneErrorToRHF(path, 'Not a valid number.', setError);
				}
			}
		});

		uniqueFields.forEach((field) => {
			const rawValue = row[field];
			if (rawValue !== undefined && rawValue !== null && rawValue !== '' && (typeof rawValue === 'string' || typeof rawValue === 'number')) {
				let normalizedValue =
					typeof rawValue === 'number' || isValidNumber(rawValue)
						? String(Number(rawValue))
						: String(rawValue);

				if (!uniqueCaseSensitive) {
					normalizedValue = normalizedValue.toLowerCase();
				}

				const map = seenMap[field] ?? {};
				const existing = map[normalizedValue];
				if (existing) {
					existing.push(rowIndex);
				} else {
					map[normalizedValue] = [rowIndex];
				}
				seenMap[field] = map;
			}
		});
	});

	uniqueFields.forEach((field) => {
		const seen = seenMap[field];
		if (!seen) return;

		for (const value in seen) {
			if (value) {
				const indexes = seen[value];
				if (indexes.length > 1) {
					indexes.forEach((i) => {
						const label = (fieldNameMapper as Record<string, string>)[String(field)] ?? String(field);
						const message = `${label} "${value}" is not unique.`;
						const path = `${String(arrayKey)}.${i}.${String(field)}` as FieldPath<TFormValues>;
						setOneErrorToRHF(path, message, setError);
					});
				}
			}
		}
	});
}

